// Assembles a manuscript — a main record plus the records it embeds — into a
// Typst project.
//
// Records are translated independently and cached by body. Assembly is
// global: it decides which @-keys are cross-references and which are
// citations, runs citeproc over every cluster in document order (so ibid,
// disambiguation and note numbers are right across chapters), resolves
// embeds and images against the collection, substitutes placeholders and
// shifts source maps to match.
import { embeddedQuotation, isAnnotation } from "./annotations.js";
import type { CiteItem } from "./cite-items.js";
import { Citeproc, type Bibliography, type CitationRequest, type CiteprocMode, type CslItem } from "./citeproc.js";
import { typstLabel, typstString } from "./escape.js";
import { IMAGE_EXTENSION, resolveLinkTarget, type WriterRecord } from "./records.js";
import { HEADER, PLACEHOLDER, translateInline, translateRecord, type Diagnostic, type TranslatedRecord } from "./translate.js";

import { manuscriptMeta, typstLanguage, type ManuscriptMeta, type MetaField } from "./meta.js";

export const MAIN = "/__main__.typ";

/** Collection path of a record → path of its generated Typst file. */
export const typstPathFor = (recordPath: string) => `/${recordPath.replace(/\.md$/i, "")}.typ`;

export interface SourceMap {
  readonly record: string;
  /** Flat pairs [typstOffset, bodyOffset, …]. */
  readonly anchors: readonly number[];
}

export interface AssemblyDiagnostic extends Diagnostic {
  readonly record: string;
  /** Set when the problem is in a manuscript setting (frontmatter) rather than the body. */
  readonly field?: MetaField;
  /** Missing collection data is not definitive until this discovery domain is ready. */
  readonly metadata?: "index" | "library" | "annotations";
  /** Set when a citation names a citekey or label that does not exist, for suggesting others. */
  readonly unknown?: { readonly kind: "citekey" | "label"; readonly key: string };
}


export interface AssemblyInput {
  readonly main: string;
  /** Records loaded so far, by collection path. */
  readonly records: ReadonlyMap<string, WriterRecord>;
  /** Every Markdown record path in the collection (for resolving embeds). */
  readonly recordPaths: ReadonlySet<string>;
  /** Every non-record file path in the collection (for resolving images). */
  readonly filePaths: ReadonlySet<string>;
  readonly library: ReadonlyMap<string, CslItem>;
  /** Bundled citation styles by id. */
  readonly styles: ReadonlyMap<string, string>;
  /** CSL locales by tag (en-US, en-GB, …). */
  readonly locales: ReadonlyMap<string, string>;
  /** Collection text files loaded so far (a .csl style or .typ template the settings name), by path. */
  readonly texts?: ReadonlyMap<string, string>;
  /** Records the collection lists as Reader annotations (embedding one quotes it). */
  readonly annotationPaths?: ReadonlySet<string>;
  /** Source record path → citekey, for citing embedded annotations. */
  readonly sourceKeys?: ReadonlyMap<string, string>;
}

export interface Assembly {
  readonly meta: ManuscriptMeta;
  /** Typst path → source. */
  readonly sources: ReadonlyMap<string, string>;
  readonly maps: ReadonlyMap<string, SourceMap>;
  /** Records in include order (main first); embedded annotations are not among them. */
  readonly order: readonly string[];
  /** Embedded records that exist in the collection but are not loaded yet. */
  readonly unloaded: readonly string[];
  /** Collection files the document uses: images, and a style or template the settings name. */
  readonly assets: readonly string[];
  /** Line (0-based) of the main Typst file → the setting written on it. */
  readonly mainFields: ReadonlyMap<number, MetaField>;
  /** Raw Typst blocks per Typst path, as [from, to, record, bodyFrom] after substitution. */
  readonly rawBlocks: ReadonlyMap<string, readonly (readonly [number, number, number])[]>;
  readonly diagnostics: readonly AssemblyDiagnostic[];
  readonly labels: ReadonlySet<string>;
  readonly citations: { readonly mode: CiteprocMode; readonly ms: number; readonly clusters: number };
  /** Rendered clusters in document order (for tests) and bibliography entries (for tests and the editor's hovers). */
  readonly debug: { readonly citations: readonly string[]; readonly bibliography: readonly { key: string; text: string }[] };
}

type Plan =
  | { kind: "text"; text: string }
  | {
      kind: "cite";
      request: number;
      narrative?: CiteItem;
      first?: boolean;
      footnote: boolean;
      inNote: boolean;
      bracketed: boolean;
      /** The cited keys and where the citation is in its record's body. */
      keys: readonly string[];
      from: number;
    };

interface Rendered {
  text: string;
  /** A generated note: moves after following punctuation (Pandoc's notes-after-punctuation). */
  note?: boolean;
}

export class ManuscriptAssembler {
  private readonly translations = new Map<string, { body: string; at?: number; tr: TranslatedRecord }>();
  private readonly engines = new Map<string, Citeproc>();

  /** Translates a record's body, or (with `at`) Markdown standing in for an embed at that offset. */
  private translate(path: string, body: string, at?: number): TranslatedRecord {
    const cached = this.translations.get(path);
    if (cached && cached.body === body && cached.at === at) return cached.tr;
    const tr = translateRecord(body, at === undefined ? {} : { at });
    this.translations.set(path, { body, ...(at === undefined ? {} : { at }), tr });
    return tr;
  }

  private engine(input: AssemblyInput, meta: ManuscriptMeta): Citeproc {
    const styleXml = input.styles.get(meta.style) ?? input.texts?.get(meta.style) ?? "";
    const key = `${meta.style}\u0000${meta.locale}\u0000${meta.forceLocale}\u0000${styleXml.length}`;
    let engine = this.engines.get(key);
    if (!engine) {
      engine = new Citeproc(styleXml, input.locales, input.library, meta.locale, meta.forceLocale);
      this.engines.set(key, engine);
    }
    engine.updateLibrary(input.library);
    return engine;
  }

  assemble(input: AssemblyInput): Assembly {
    const diagnostics: AssemblyDiagnostic[] = [];
    const mainRecord = input.records.get(input.main);
    const { meta, problems, needs } = manuscriptMeta(mainRecord?.frontmatter ?? {}, {
      styles: input.styles,
      locales: new Set(input.locales.keys()),
      main: input.main,
      filePaths: input.filePaths,
      ...(input.texts ? { texts: input.texts } : {}),
    });
    for (const p of problems) diagnostics.push({ record: input.main, from: 0, to: 0, severity: "warning", message: p.message, field: p.field });

    // Include order, depth-first as Typst evaluates it. An embedded
    // annotation is included as its quotation, which maps to the embed in the
    // record holding it (its host).
    const order: string[] = [];
    const files: string[] = [];
    const unloaded: string[] = [];
    const includeTargets = new Map<string, (string | null)[]>();
    const translated = new Map<string, TranslatedRecord>();
    const hosts = new Map<string, string>();
    const recordOf = (path: string) => hosts.get(path) ?? path;
    const visit = (path: string, stack: readonly string[], embed?: { host: string; from: number; to: number }) => {
      const record = input.records.get(path);
      if (!record) {
        if (!unloaded.includes(path)) unloaded.push(path);
        return;
      }
      if (files.includes(path)) return;
      files.push(path);
      if (embed && isAnnotation(record, input.annotationPaths)) {
        hosts.set(path, embed.host);
        const quotation = embeddedQuotation(record, input.sourceKeys ?? new Map());
        if (quotation.problem) diagnostics.push({ record: embed.host, from: embed.from, to: embed.to, ...quotation.problem });
        translated.set(path, this.translate(path, quotation.markdown, embed.from));
        return;
      }
      order.push(path);
      const tr = this.translate(path, record.body);
      translated.set(path, tr);
      const targets = tr.includes.map((inc) => {
        const resolved = resolveLinkTarget(inc.target, path, input.recordPaths);
        if (!resolved) {
          diagnostics.push({ record: path, from: inc.from, to: inc.to, severity: "error", metadata: "index", message: `No record matches the embed ![[${inc.target}]].` });
          return null;
        }
        if (resolved === path || stack.includes(resolved)) {
          diagnostics.push({ record: path, from: inc.from, to: inc.to, severity: "error", message: `Embedding ${resolved} here would include it inside itself.` });
          return null;
        }
        return resolved;
      });
      includeTargets.set(path, targets);
      targets.forEach((t, i) => {
        const inc = tr.includes[i];
        if (t && inc) visit(t, [...stack, path], { host: path, from: inc.from, to: inc.to });
      });
    };
    visit(input.main, []);

    for (const [path, tr] of translated) for (const d of tr.diagnostics) diagnostics.push({ record: recordOf(path), ...d });
    const labels = new Set<string>();
    for (const [path, tr] of translated) {
      for (const label of tr.labels) {
        if (labels.has(label)) diagnostics.push({ record: recordOf(path), from: 0, to: 0, severity: "warning", message: `The label ${label} is used more than once.` });
        labels.add(label);
      }
    }

    // Citations: walk every record's events in document order.
    const cp = this.engine(input, meta);
    const plans = new Map<string, Plan[]>();
    const requests: CitationRequest[] = [];
    let note = 0;
    let inFootnote: number | null = null;
    const seen = new Set<string>();
    for (const path of files) {
      const tr = translated.get(path) as TranslatedRecord;
      const plan: Plan[] = [];
      plans.set(path, plan);
      for (const ev of tr.events) {
        if (ev.t === "fn-open") {
          note++;
          inFootnote = note;
          continue;
        }
        if (ev.t === "fn-close") {
          inFootnote = null;
          continue;
        }
        const cl = tr.clusters[ev.index];
        if (!cl) continue;
        const refs = cl.items.filter((i) => labels.has(i.key));
        if (refs.length && refs.length === cl.items.length) {
          plan[ev.index] = {
            kind: "text",
            text: cl.items
              .map((i) => `${i.prefix ? `${looseEscape(i.prefix)} ` : ""}#ref(<${typstLabel(i.key)}>);${i.suffix ? looseEscape(i.suffix) : ""}`)
              .join(", "),
          };
          continue;
        }
        for (const m of cl.items.filter((i) => !labels.has(i.key) && !cp.has(i.key))) {
          const looksLikeXref = /^(sec|fig|tbl|eq|lst|thm|lem|def)-/.test(m.key);
          diagnostics.push({
            record: recordOf(path),
            from: cl.from,
            to: cl.to,
            severity: "error",
            message: looksLikeXref ? `Nothing is labelled ${m.key}.` : `No source in the library has the citekey ${m.key}.`,
            unknown: { kind: looksLikeXref ? "label" : "citekey", key: m.key },
            ...(looksLikeXref ? {} : { metadata: "library" as const }),
          });
        }
        const items = cl.items.filter((i) => cp.has(i.key));
        if (!items.length || refs.length) {
          if (refs.length && items.length) {
            diagnostics.push({ record: recordOf(path), from: cl.from, to: cl.to, severity: "error", message: "A citation cannot mix cross-references and citekeys." });
          }
          plan[ev.index] = { kind: "text", text: cl.items.map((i) => `#missing(${typstString(i.key)});`).join(", ") };
          continue;
        }
        const inNote = inFootnote !== null;
        const footnote = cp.isNoteStyle && !inNote;
        if (footnote) note++;
        // Narrative @key: the author in the text, the rest with the author
        // suppressed. Inside a footnote of a note style Pandoc renders the full
        // citation inline instead.
        const narrative = !cl.bracketed && !(cp.isNoteStyle && inNote) ? items[0] : undefined;
        const first = narrative ? !seen.has(narrative.key) : undefined;
        for (const i of items) seen.add(i.key);
        requests.push({
          items: narrative ? [{ ...narrative, suppressAuthor: true }] : items,
          noteIndex: cp.isNoteStyle ? (inFootnote ?? note) : (inFootnote ?? 0),
        });
        plan[ev.index] = {
          kind: "cite",
          request: requests.length - 1,
          footnote,
          inNote: cp.isNoteStyle && inNote,
          bracketed: cl.bracketed,
          keys: items.map((i) => i.key),
          from: cl.from,
          ...(narrative ? { narrative } : {}),
          ...(first !== undefined ? { first } : {}),
        };
      }
    }
    const result = cp.process(requests);

    // `marked` puts source markers in a generated note, so a click on it in the preview finds its sources.
    const render = (p: Plan | undefined, marked = false): Rendered => {
      if (!p) return { text: "" };
      if (p.kind === "text") return { text: p.text };
      const note = (body: string) => (marked ? `${noteMarker(p.keys, p.from)}${body}${END_MARKER}` : body);
      let s = result.strings[p.request] ?? "";
      // Inside a note the surrounding sentence supplies punctuation.
      if (p.inNote) s = s.replace(/\.$/, "");
      if (p.narrative) {
        const author = cp.authorOnly(p.narrative.key, p.first ?? false);
        if (cp.isNoteStyle) return { text: `${author}#footnote[${note(s)}];` };
        return { text: author ? `${author} ${s}` : s };
      }
      if (p.inNote && p.bracketed) return { text: `(${s})` };
      if (p.footnote) return { text: `#footnote[${note(s.replace(/^[a-z]/, (c) => c.toUpperCase()))}];`, note: true };
      return { text: s };
    };

    // Images: resolve against the collection's files.
    const assets = new Set<string>(needs);
    if (meta.customTemplate) assets.add(meta.template);
    const imageExpression = (path: string, index: number): string => {
      const ref = (translated.get(path) as TranslatedRecord).images[index];
      if (!ref) return "none";
      const remote = /^[a-z]+:\/\//i.test(ref.target);
      const resolved = remote ? null : resolveLinkTarget(ref.target, path, input.filePaths, "");
      if (!resolved || !IMAGE_EXTENSION.test(resolved)) {
        diagnostics.push({
          record: recordOf(path),
          from: ref.from,
          to: ref.to,
          severity: "error",
          ...(remote ? {} : { metadata: "index" as const }),
          message: remote ? `Images must be files in the collection (${ref.target}).` : `No file in the collection matches the image ${ref.target}.`,
        });
        return `missing-image(${typstString(ref.target)})`;
      }
      assets.add(resolved);
      return `image(${typstString(`/${resolved}`)}${ref.width ? `, width: ${ref.width}` : ""})`;
    };

    const sources = new Map<string, string>();
    const maps = new Map<string, SourceMap>();
    const rawBlocks = new Map<string, (readonly [number, number, number])[]>();
    const debugCitations: string[] = [];
    for (const path of files) {
      const tr = translated.get(path) as TranslatedRecord;
      const plan = plans.get(path) ?? [];
      for (const p of plan) if (p?.kind === "cite") debugCitations.push(render(p).text);
      const targets = includeTargets.get(path) ?? [];
      const substituted = substitute(tr, {
        cluster: (i) => render(plan[i], true),
        image: (i) => imageExpression(path, i),
        include: (i) => {
          const t = targets[i];
          // An embedded record that is still loading is left out until it arrives.
          return t && input.records.has(t) ? `#include ${typstString(typstPathFor(t))}` : "";
        },
      });
      const bound = bindRecordPath(substituted, recordOf(path));
      sources.set(typstPathFor(path), bound.text);
      maps.set(typstPathFor(path), { record: recordOf(path), anchors: bound.anchors });
      rawBlocks.set(
        typstPathFor(path),
        tr.rawBlocks.map((b) => [bound.shift(b.typstFrom), bound.shift(b.typstTo), b.from] as const),
      );
    }
    const main = mainSource(input.main, meta, result.bibliography);
    sources.set(MAIN, main.text);

    return {
      meta,
      sources,
      maps,
      order,
      unloaded,
      assets: [...assets],
      mainFields: main.fields,
      rawBlocks,
      diagnostics,
      labels,
      citations: { mode: result.mode, ms: result.ms, clusters: requests.length },
      debug: {
        citations: debugCitations,
        bibliography: result.bibliography.entries.map((e) => ({ key: e.key, text: (e.label ? `${e.label} ` : "") + e.body })),
      },
    };
  }
}

/**
 * Source markers, for finding what a click in the preview landed on: one
 * opens a bibliography entry (its key) or a generated note (its keys, and the
 * record and body offset of its citation); END_MARKER closes it.
 */
const entryMarker = (key: string) => `#metadata(("entry", ${typstString(key)}))<md-ref>`;
const noteMarker = (keys: readonly string[], from: number) => `#metadata(("note", md-file, ${from}, (${keys.map(typstString).join(", ")},)))<md-ref>`;
const END_MARKER = `#metadata(("end",))<md-ref>`;

function looseEscape(s: string): string {
  return s.replace(/[\\#*_`$@<>[\]~]/g, "\\$&");
}

function mainSource(mainPath: string, meta: ManuscriptMeta, bib: Bibliography): { text: string; fields: Map<number, MetaField> } {
  const authors = meta.authors
    .map((a) => `(name: [${translateInline(a.name)}]${a.affiliation ? `, affiliation: [${translateInline(a.affiliation)}]` : ""})`)
    .join(", ");
  const language = typstLanguage(meta.lang);
  // A collection template is a .typ file defining `template`, with the same
  // parameters as the bundled ones.
  const template = meta.customTemplate ? "writer-template" : meta.template;
  const lines: [string, MetaField?][] = [
    [HEADER.trimEnd()],
    meta.customTemplate ? [`#import ${typstString(`/${meta.template}`)}: template as writer-template`, "template"] : [`#import "/templates/${meta.template}.typ": *`, "template"],
    [`#set text(lang: ${typstString(language.lang)}${language.region ? `, region: ${typstString(language.region)}` : ""})`, "lang"],
    [`#show: ${template}.with(`, "template"],
    meta.title ? [`  title: [${translateInline(meta.title)}],`, "title"] : [""],
    meta.subtitle ? [`  subtitle: [${translateInline(meta.subtitle)}],`, "subtitle"] : [""],
    [`  authors: (${authors}${meta.authors.length === 1 ? "," : ""}),`, "authors"],
    meta.abstract ? [`  abstract: [${translateInline(meta.abstract)}],`, "abstract"] : [""],
    meta.date ? [`  date: ${typstString(meta.date)},`, "date"] : [""],
    [`)`, "template"],
    [`#include ${typstString(typstPathFor(mainPath))}`],
  ];
  if (bib.entries.length) {
    const entries = bib.entries.map((e) => {
      const anchor = `#metadata(none)<ref-${typstLabel(e.key)}>${entryMarker(e.key)}`;
      return bib.secondFieldAlign && e.label !== undefined ? `([${anchor}${e.label}], [${e.body}${END_MARKER}])` : `[${anchor}${e.body}${END_MARKER}]`;
    });
    lines.push([`#bibliography-list(hanging: ${bib.hangingIndent}, (\n${entries.join(",\n")},\n))`, "csl"]);
  }
  // One query target listing every block marker's page position (preview click → source),
  // and one for the source markers around bibliography entries and generated notes.
  lines.push([`#context [#metadata(query(<md-src>).map(m => (m.value, m.location().position()))) <md-pos>]`]);
  lines.push([`#context [#metadata(query(<md-ref>).map(m => (m.value, m.location().position()))) <md-refs>]`]);
  const fields = new Map<number, MetaField>();
  const out: string[] = [];
  let line = 0;
  for (const [text, field] of lines) {
    if (!text) continue;
    if (field) for (let i = 0; i <= (text.match(/\n/g)?.length ?? 0); i++) fields.set(line + i, field);
    line += (text.match(/\n/g)?.length ?? 0) + 1;
    out.push(text);
  }
  return { text: `${out.join("\n")}\n`, fields };
}

interface Substitutions {
  cluster(index: number): Rendered;
  image(index: number): string;
  include(index: number): string;
}

const PLACEHOLDERS = new RegExp(
  `(${PLACEHOLDER.cluster[0]}|${PLACEHOLDER.image[0]}|${PLACEHOLDER.include[0]})(\\d+)[${PLACEHOLDER.cluster[1]}${PLACEHOLDER.image[1]}${PLACEHOLDER.include[1]}]`,
  "g",
);

/** Replaces placeholders; returns the text and a function mapping old offsets to new ones. */
function substitute(tr: TranslatedRecord, subs: Substitutions): { text: string; anchors: number[]; shift: (offset: number) => number } {
  const src = tr.typst;
  const parts: string[] = [];
  const shifts: [number, number][] = []; // [old offset, cumulative delta from there on]
  let last = 0;
  let delta = 0;
  PLACEHOLDERS.lastIndex = 0;
  for (let m = PLACEHOLDERS.exec(src); m; m = PLACEHOLDERS.exec(src)) {
    let before = src.slice(last, m.index);
    const index = Number(m[2]);
    let end = m.index + m[0].length;
    let text: string;
    if (m[1] === PLACEHOLDER.cluster[0]) {
      const r = subs.cluster(index);
      text = r.text;
      if (r.note) {
        const trimmed = before.replace(/[ \t]+$/, "");
        delta -= before.length - trimmed.length;
        before = trimmed;
        const punct = /^[.,;:!?]+/.exec(src.slice(end));
        if (punct) {
          text = punct[0] + text;
          end += punct[0].length;
          delta -= punct[0].length;
        }
      }
    } else if (m[1] === PLACEHOLDER.image[0]) text = subs.image(index);
    else text = subs.include(index);
    parts.push(before, text);
    delta += text.length - m[0].length;
    shifts.push([end, delta]);
    last = end;
    PLACEHOLDERS.lastIndex = end;
  }
  parts.push(src.slice(last));
  const shift = (offset: number) => {
    let d = 0;
    for (const [at, value] of shifts) {
      if (at <= offset) d = value;
      else break;
    }
    return offset + d;
  };
  const anchors = [...tr.anchors];
  for (let i = 0; i < anchors.length; i += 2) anchors[i] = shift(anchors[i] ?? 0);
  return { text: parts.join(""), anchors, shift };
}

/** Binds `md-file` (used by source markers) right after the import header. */
function bindRecordPath(r: { text: string; anchors: number[]; shift: (o: number) => number }, record: string) {
  const line = `#let md-file = ${typstString(record)}\n`;
  const at = HEADER.length;
  const anchors = r.anchors.map((v, i) => (i % 2 === 0 && v >= at ? v + line.length : v));
  const shift = (o: number) => {
    const s = r.shift(o);
    return s >= at ? s + line.length : s;
  };
  return { text: r.text.slice(0, at) + line + r.text.slice(at), anchors, shift };
}

/** Maps an offset in a generated Typst file back to a body offset in its record. */
export function mapToRecord(map: SourceMap, typstOffset: number): number {
  const a = map.anchors;
  let lo = 0;
  let hi = a.length / 2 - 1;
  let best = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if ((a[mid * 2] ?? 0) <= typstOffset) {
      best = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  if (best < 0) return 0;
  const start = a[best * 2] ?? 0;
  const src = a[best * 2 + 1] ?? 0;
  const nextSrc = best * 2 + 3 < a.length ? (a[best * 2 + 3] ?? Infinity) : Infinity;
  // Escaping makes Typst runs slightly longer; never run past the next anchor.
  return Math.min(src + (typstOffset - start), Math.max(src, nextSrc - 1));
}

/** Converts a Typst "line:col" diagnostic position to an offset in the source. */
export function typstPositionToOffset(source: string, line: number, column: number): number {
  let offset = 0;
  let current = 1;
  for (let i = 0; i < source.length && current < line; i++) {
    if (source[i] === "\n") current++;
    offset = i + 1;
  }
  return offset + Math.max(0, column - 1);
}
